import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Button, Space, Alert, Spin, Input, Modal, Typography, Switch } from 'antd';
import { SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import Header from '../components/Header';
import ConfigPanel from '../components/ConfigPanel';
import DestinationPanel from '../components/DestinationPanel';
import AdvancedPanel from '../components/AdvancedPanel';
import PreviewPanel from '../components/PreviewPanel';
import RulesEditor from '../components/RulesEditor';
import ReferenceModal from '../components/ReferenceModal';
import ResizableDivider from '../components/ResizableDivider';
import { generateAPI, svgsAPI } from '../services/api';
import '../App.css';

const { Text } = Typography;

const DEFAULT_CONFIG = {
  design_speed: 60,
  sign_type: 'shoulder',
  mounting_form: 'Roadside',
  route_type: 'NH',
  route_number: '',
  show_dimensions: false,
  block_height_sw: 21.5,
  gap_between_blocks_sw: 1,
  top_bottom_padding_sw: 1.5,
  horizontal_inner_padding_sw: 1.5,
  siting: '750m',
};

const DEFAULT_DESTINATIONS = [
  { hindi_text: 'अहमदाबाद', english_text: 'Ahmedabad', native_text: '', number_text: '', arrow_direction: 'up', distance_km: '120', symbol: '' },
  { hindi_text: 'सूरत', english_text: 'Surat', native_text: '', number_text: '', arrow_direction: 'left', distance_km: '85', symbol: '' },
  { hindi_text: 'बारडोली', english_text: 'Bardoli', native_text: '', number_text: '', arrow_direction: 'right', distance_km: '25', symbol: '' },
];

const LIVE_PREVIEW_DEBOUNCE_MS = 550;

export default function EditorPage({ user, onLogout }) {
  const { svgId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('config');
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [destinations, setDestinations] = useState(DEFAULT_DESTINATIONS);
  const [svgContent, setSvgContent] = useState('');
  const [signInfo, setSignInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingSaved, setLoadingSaved] = useState(!!svgId);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savingSVG, setSavingSVG] = useState(false);
  const [savedTitle, setSavedTitle] = useState('');
  const [currentSavedId, setCurrentSavedId] = useState(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('leftPanelWidth');
    return saved ? parseInt(saved, 10) : 480;
  });
  const [rulesOpen, setRulesOpen] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [livePreview, setLivePreview] = useState(true);
  const [previewDraft, setPreviewDraft] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const suppressLivePreviewRef = useRef(0);

  useEffect(() => {
    if (!svgId) {
      setLoadingSaved(false);
      setCurrentSavedId(null);
      setSavedTitle('');
      setConfig(DEFAULT_CONFIG);
      setDestinations(DEFAULT_DESTINATIONS);
      setSvgContent('');
      setSignInfo({});
      setPreviewDraft(false);
      suppressLivePreviewRef.current = 0;
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingSaved(true);
      try {
        const res = await svgsAPI.get(svgId);
        const saved = res.data;
        if (cancelled) return;

        setCurrentSavedId(saved.id);
        setSavedTitle(saved.name || saved.title || 'Untitled');

        setConfig({
          design_speed: saved.design_speed || 60,
          sign_type: saved.sign_type || 'shoulder',
          mounting_form: saved.mounting_form || 'Roadside',
          route_type: saved.route_type || 'NH',
          route_number: saved.route_number || '',
          show_dimensions: !!saved.show_dimensions,
          block_height_sw: 21.5,
          gap_between_blocks_sw: 1,
          top_bottom_padding_sw: 1.5,
          horizontal_inner_padding_sw: 1.5,
          siting: '750m',
        });

        const dest = saved.destinations?.length ? saved.destinations : DEFAULT_DESTINATIONS;
        setDestinations(
          dest.map((d, index) => {
            const defaultArrows = ['up', 'left', 'right'];
            return {
              hindi_text: d.hindi_text || '',
              english_text: d.english_text || '',
              native_text: d.native_text || '',
              number_text: d.number_text || '',
              arrow_direction: d.arrow_direction || defaultArrows[index] || 'up',
              distance_km: d.distance_km || '',
              symbol: d.symbol || '',
            };
          })
        );

        if (saved.svg_content) setSvgContent(saved.svg_content);

        const cj = saved.config_json || {};
        setSignInfo({
          dimensions: cj.dimensions || saved.design_speed + ' km/h',
          strokeWidth: cj.strokeWidth || '--',
          xHeight: cj.xHeight || '--',
          signType: saved.sign_type || 'shoulder',
        });
        setPreviewDraft(false);
        setError('');
      } catch (e) {
        if (!cancelled) setError('Failed to load saved SVG');
      } finally {
        if (!cancelled) {
          setLoadingSaved(false);
          suppressLivePreviewRef.current = svgId ? 1 : 0;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [svgId]);

  const updateConfig = useCallback((updates) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const destinationsKey = useMemo(
    () =>
      JSON.stringify(
        destinations.map((d) => ({
          hi: d.hindi_text,
          en: d.english_text,
          nt: d.native_text,
          num: d.number_text,
          ar: d.arrow_direction,
          km: d.distance_km,
          sym: d.symbol,
          ip: d.image_path,
          lp: d.logo_path,
        }))
      ),
    [destinations]
  );

  const buildGeneratePayload = useCallback(
    () => ({
      design_speed: config.design_speed,
      sign_type: config.sign_type,
      show_dimensions: config.show_dimensions,
      destinations: destinations.map((d) => ({
        hindi_text: d.hindi_text || '',
        english_text: d.english_text || '',
        native_text: d.native_text || '',
        number_text: d.number_text || '',
        arrow_direction: d.arrow_direction || 'up',
        distance_km: d.distance_km || '',
        symbol: d.symbol || '',
        image_path: d.image_path || '',
        logo_path: d.logo_path || '',
      })),
    }),
    [config.design_speed, config.sign_type, config.show_dimensions, destinations]
  );

  const hydrateMetricsFromStdout = useCallback((stdout) => {
    const xLower = stdout.match(/x_height_lower:\s*([\d.]+)/)?.[1] || '';
    const xUpper = stdout.match(/x_height_upper:\s*([\d.]+)/)?.[1] || '';
    const sw = stdout.match(/stroke_width:\s*([\d.]+)/)?.[1] || '';
    setSignInfo({
      dimensions: xLower ? `${xLower}mm base` : config.design_speed + ' km/h',
      strokeWidth: sw ? `${parseFloat(sw).toFixed(2)}mm` : '--',
      xHeight: xLower && xUpper ? `${xLower}/${xUpper}mm` : '--',
      signType: config.sign_type,
    });
  }, [config.design_speed, config.sign_type]);

  useEffect(() => {
    if (!livePreview || loadingSaved || !user) return undefined;

    const t = window.setTimeout(async () => {
      if (suppressLivePreviewRef.current > 0) {
        suppressLivePreviewRef.current -= 1;
        return;
      }

      try {
        setPreviewLoading(true);
        const res = await generateAPI.preview(buildGeneratePayload());
        if (res.data?.status === 'success' && res.data.svg) {
          setSvgContent(res.data.svg);
          hydrateMetricsFromStdout(res.data.stdout || '');
          setPreviewDraft(true);
        }
      } catch (err) {
        const detail =
          err.response?.data?.details || err.response?.data?.error || err.message || 'Preview failed';
        console.warn('[live preview]', detail);
      } finally {
        setPreviewLoading(false);
      }
    }, LIVE_PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [
    livePreview,
    loadingSaved,
    user,
    config.design_speed,
    config.sign_type,
    config.show_dimensions,
    destinationsKey,
    buildGeneratePayload,
    hydrateMetricsFromStdout,
  ]);

  const persistPayload = () => ({
    title: savedTitle || 'Untitled',
    design_speed: config.design_speed,
    sign_type: config.sign_type,
    mounting_form: config.mounting_form,
    route_type: config.route_type,
    route_number: config.route_number,
    show_dimensions: config.show_dimensions,
    destinations: destinations.map((d) => ({
      hindi_text: d.hindi_text || '',
      english_text: d.english_text || '',
      native_text: d.native_text || '',
      number_text: d.number_text || '',
      arrow_direction: d.arrow_direction || 'up',
      distance_km: d.distance_km || '',
      symbol: d.symbol || '',
      image_path: d.image_path || '',
      logo_path: d.logo_path || '',
    })),
    config_json: { ...config, ...signInfo },
    svg_content: svgContent || ' ',
  });

  const handleGenerate = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await generateAPI.generate(buildGeneratePayload());

      if (res.data.status === 'success' && res.data.svg) {
        setSvgContent(res.data.svg);

        const stdout = res.data.stdout || '';
        const xLower = stdout.match(/x_height_lower:\s*([\d.]+)/)?.[1] || '';
        const xUpper = stdout.match(/x_height_upper:\s*([\d.]+)/)?.[1] || '';
        const sw = stdout.match(/stroke_width:\s*([\d.]+)/)?.[1] || '';

        const newSignInfo = {
          dimensions: xLower ? `${xLower}mm base` : config.design_speed + ' km/h',
          strokeWidth: sw ? `${parseFloat(sw).toFixed(2)}mm` : '--',
          xHeight: xLower && xUpper ? `${xLower}/${xUpper}mm` : '--',
          signType: config.sign_type,
        };
        setSignInfo(newSignInfo);
        updateConfig({
          dimensions: newSignInfo.dimensions,
          strokeWidth: newSignInfo.strokeWidth,
          xHeight: newSignInfo.xHeight,
        });
        setPreviewDraft(false);

        const nid = res.data.saved_svg_id;
        const ntitle = res.data.saved_svg_title;
        if (nid) {
          setCurrentSavedId(nid);
          if (ntitle) setSavedTitle(ntitle);
          navigate(`/editor/${nid}`, { replace: true });
        }
      } else {
        setError('Generation returned empty result');
      }
    } catch (err) {
      const data = err.response?.data;
      let detail = '';
      if (data?.details) {
        const lines = data.details.split('\n').filter((l) => l.trim());
        const lastLine = lines[lines.length - 1] || '';
        detail = lastLine.includes('Error') ? lastLine : data.details.slice(0, 300);
      } else {
        detail = data?.error || err.message;
      }
      setError(`Generation failed (${config.sign_type}): ${detail}`);
    }
    setIsLoading(false);
  }, [config, navigate, updateConfig, buildGeneratePayload]);

  const handleSaveChanges = async () => {
    if (!currentSavedId) {
      Modal.info({
        title: 'Save to library first',
        content:
          'Turn on Live preview for free previews. When ready, click "Generate & save to library" to create your first saved entry — that step uses your quota. Afterwards use Save changes to persist edits without a new quota hit.',
      });
      return;
    }
    if (!svgContent?.trim()) {
      setError('Nothing to save');
      return;
    }
    setSaving(true);
    setSaveSuccess(false);
    setError('');
    try {
      await svgsAPI.update(currentSavedId, persistPayload());
      setSaveSuccess(true);
      setPreviewDraft(false);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const tabItems = [
    { key: 'config', label: 'Configuration', children: <ConfigPanel config={config} onChange={updateConfig} /> },
    {
      key: 'destinations',
      label: 'Destinations',
      children: <DestinationPanel destinations={destinations} onChange={setDestinations} signType={config.sign_type} />,
    },
    { key: 'advanced', label: 'Advanced', children: <AdvancedPanel config={config} onChange={updateConfig} /> },
  ];

  if (loadingSaved) {
    return (
      <div className="app">
        <Header
          onOpenRules={() => setRulesOpen(true)}
          onOpenLibrary={() => navigate('/svgs')}
          onOpenReference={() => setReferenceOpen(true)}
          onNewEditor={() => navigate('/editor')}
          user={user}
          onLogout={onLogout}
        />
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
          <Spin size="large" />
          <div style={{ color: 'var(--text-secondary)', marginTop: 16 }}>Loading saved sign…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        onOpenRules={() => setRulesOpen(true)}
        onOpenLibrary={() => navigate('/svgs')}
        onOpenReference={() => setReferenceOpen(true)}
        onNewEditor={() => navigate('/editor')}
        user={user}
        onLogout={onLogout}
      />

      <div className={`app-main ${isMobile ? 'mobile-layout' : ''}`}>
        <div className="left-panel" style={!isMobile ? { width: `${leftPanelWidth}px`, flexShrink: 0 } : {}}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            className="editor-tabs"
            type={isMobile ? 'card' : 'line'}
          />

          <div className="left-panel-editor-footer">
          <div style={{ padding: '0 16px 8px', maxWidth: '100%' }}>
            <Space orientation="vertical" style={{ width: '100%' }} size={4}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Title (library)</span>
              <Input
                value={savedTitle}
                onChange={(e) => setSavedTitle(e.target.value)}
                placeholder="Name this sign…"
              />
              {currentSavedId && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Saved ID: {currentSavedId}
                </Text>
              )}
            </Space>
          </div>

          <div className="action-bar">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Space size="small">
                <Text style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Live preview</Text>
                <Switch checked={livePreview} onChange={setLivePreview} size="small" />
              </Space>
              <Text type="secondary" style={{ fontSize: 11, maxWidth: 320 }}>
                Server render (~0.6s settle) • no quota. Download after save.
              </Text>
            </div>
            {saveSuccess && (
              <Alert
                message="Saved"
                type="success"
                showIcon
                closable
                style={{ marginBottom: 12 }}
                onClose={() => setSaveSuccess(false)}
              />
            )}
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                style={{ marginBottom: 12 }}
                onClose={() => setError('')}
              />
            )}
            <Space orientation={isMobile ? 'vertical' : 'horizontal'} size="middle" style={{ width: '100%' }}>
              <Button
                icon={<SaveOutlined />}
                onClick={handleSaveChanges}
                loading={saving}
                block={isMobile}
                style={!isMobile ? { flex: 1 } : {}}
              >
                Save changes
              </Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={handleGenerate}
                loading={isLoading}
                block={isMobile}
                style={!isMobile ? { flex: 1 } : {}}
              >
                Generate & save to library
              </Button>
            </Space>
          </div>
          </div>
        </div>

        {!isMobile && <ResizableDivider onResize={setLeftPanelWidth} minWidth={300} maxWidth={800} />}

        <div className="right-panel">
          <PreviewPanel
            svgContent={svgContent}
            signInfo={signInfo}
            isLoading={isLoading}
            previewRefreshing={previewLoading}
            exportLocked={previewDraft}
            livePreviewHint={
              livePreview
                ? 'Changes preview automatically shortly after you stop editing'
                : 'Turn Live preview on, or Generate & save to library'
            }
            projectId={currentSavedId}
            savingSVG={savingSVG}
            onSaveSVG={async () => {
              await handleSaveChanges();
            }}
          />
        </div>
      </div>

      <RulesEditor isOpen={rulesOpen} onClose={() => setRulesOpen(false)} />
      <ReferenceModal isOpen={referenceOpen} onClose={() => setReferenceOpen(false)} />
    </div>
  );
}
